import { Injectable } from '@angular/core';
import { ChartData, ChartDataItem } from '../../model/chart/chart-data.model';
import {Platform} from "../../model/product/product.model";

@Injectable( {
  providedIn: 'root',
} )
export class UtilService {

  KILO = 1000;
  MILLION = this.KILO * this.KILO;

  constructor() {
    // Intentionally blank
  }

  isTouchDevice() {
    return ( ( 'ontouchstart' in window ) ||
      ( navigator.maxTouchPoints > 0 ) );
  }

  isSmallScreen() {
    return window.matchMedia && window.matchMedia( '(max-width: 999px)' ).matches;
  }

  /**
   * True only when the primary pointer can actually hover (mouse / trackpad),    * the JS twin of the CSS `@media (hover: hover)` guard. Used so hover-to-open
   * menus don't misfire on touchscreens: there a single tap synthesizes
   * `mouseenter` + `click`, which would otherwise open the menu on hover and
   * immediately toggle it shut on the click (the "first tap does nothing,
   * second tap works" bug). Width is the wrong signal, a touch tablet wider
   * than 999px still can't hover.
   */
  supportsHover() {
    return window.matchMedia && window.matchMedia( '(hover: hover) and (pointer: fine)' ).matches;
  }

  shuffle( array: any[] ) {
    let currentIndex = array.length, randomIndex;

    while ( currentIndex !== 0 ) {
      randomIndex = Math.floor( Math.random() * currentIndex );
      currentIndex--;

      [ array[ currentIndex ], array[ randomIndex ] ] = [
        array[ randomIndex ], array[ currentIndex ] ];
    }

    return array;
  }

  delay( ms: number ) {
    return new Promise( ( resolve ) => setTimeout( resolve, ms ) );
  }

  numberFormatter( n: number ) {
    if ( n < this.KILO ) {
      return n + '';
    } else {
      if ( n < this.MILLION ) {
        return Math.round( ( n / this.KILO ) * 10 ) / 10 + 'k';
      } else {
        return Math.round( ( n / this.MILLION ) * 10 ) / 10 + 'M';
      }
    }
  }

  formatDate = ( date ) => {
    const d = new Date( date );
    const year = d.getFullYear();
    let month = '' + ( d.getMonth() + 1 );
    let day = '' + d.getDate();

    if ( month.length < 2 ) {
      month = '0' + month;
    }

    if ( day.length < 2 ) {
      day = '0' + day;
    }

    return [ year, month, day ].join( '-' );
  };

  corsRequest = ( uri: string ) => {
    return new Promise( ( resolve, reject ) => {
      const xmlHttpRequest = new XMLHttpRequest();
      xmlHttpRequest.onreadystatechange = function () {
        if ( this.readyState === 4 ) {
          if ( this.status === 200 ) {
            resolve( this.responseText );
          } else {
            reject( null );
          }
        }
      };
      xmlHttpRequest.open( 'GET', uri );
      xmlHttpRequest.send();
    } );
  };

  calculateRotation( index: number, chartData: ChartData, maxDegree = 360 ) {
    let rotation = 0;

    for ( const item of chartData.items ) {
      if ( chartData.items.indexOf( item ) < index ) {
        rotation += maxDegree * item.value / chartData.total;
      }
    }

    return rotation;
  }

  calculateClipPath( item: ChartDataItem, chartData: ChartData, maxDegree = 360 ) {
    const totalAngleInDegrees = ( maxDegree * item.value / chartData.total );

    let otherSide = 50;
    let otherAngle = 90;

    if ( totalAngleInDegrees % 90 > 45 ) {
      otherSide = 50 * 1.414;
      otherAngle = 45;
    }

    const angleInDegrees = totalAngleInDegrees % 45;
    const angleInRadian = angleInDegrees * ( Math.PI / 180 );
    const resultAngleInDegrees = 180 - otherAngle - angleInDegrees;
    const resultAngleInRadian = resultAngleInDegrees * ( Math.PI / 180 );

    const resultSide = otherSide * ( Math.sin( angleInRadian ) / Math.sin( resultAngleInRadian ) );

    if ( totalAngleInDegrees < 45 ) {
      return 'polygon(50% 50%, ' + ( 50 + resultSide ) + '% 0, 50% 0%)';
    } else if ( totalAngleInDegrees < 90 ) {
      return 'polygon(100% ' + resultSide + '%, 100% 0, 50% 0, 50% 50%)';
    } else if ( totalAngleInDegrees < 135 ) {
      return 'polygon(100% ' + ( 50 + resultSide ) + '%, 100% 0, 50% 0, 50% 50%)';
    } else if ( totalAngleInDegrees < 180 ) {
      return 'polygon(50% 0%, 100% 0, 100% 100%, ' + ( 100 - resultSide ) + '% 100%, 50% 50%)';
    } else if ( totalAngleInDegrees < 225 ) {
      return 'polygon(50% 0%, 100% 0, 100% 100%, ' + ( 50 - resultSide ) + '% 100%, 50% 50%)';
    } else if ( totalAngleInDegrees < 270 ) {
      return 'polygon(50% 0, 100% 0, 100% 100%, 0 100%, 0 ' + ( 100 - resultSide ) + '%, 50% 50%)';
    } else if ( totalAngleInDegrees < 315 ) {
      return 'polygon(50% 0, 100% 0, 100% 100%, 0 100%, 0 ' + ( 50 - resultSide ) + '%, 50% 50%)';
    } else if ( totalAngleInDegrees < 360 ) {
      return 'polygon(50% 50%, 50% 0, 100% 0, 100% 100%, 0 100%, 0 50%, 0 0, ' + ( resultSide ) + '% 0)';
    } else if ( totalAngleInDegrees === 360 ) {
      return 'polygon(50% 50%, 50% 0, 100% 0, 100% 100%, 0 100%, 0 50%, 0 0, 50% 0)';
    }
  }

  getJetbrainsExternalId(product: any ): string {
    return product?.platforms?.find( (platform: Platform) => platform.info?.name.includes('JetBrains') )?.externalId;
  }

  getChromeExternalId( product: any ): string {
    return product?.platforms?.find( (platform: Platform) => platform.info?.name.includes('Chrome') )?.externalId;
  }

  getChromeSlug( product: any ): string {
    return product?.key ? `${ product.key }-theme` : undefined;
  }

  // VS Code Marketplace uses `<publisher>.<extensionName>` as the unique
  // identifier (e.g. "codigrate.cod-aurora-borealis-theme").
  getVscodeExternalId( product: any ): string {
    return product?.platforms?.find( (platform: Platform) => platform.info?.name === 'VS Code' )?.externalId;
  }

  // Open VSX (Cursor's primary registry) identifies an extension by
  // `<namespace>/<name>`.
  getOpenVsxExternalId( product: any ): string {
    return product?.platforms?.find( (platform: Platform) => platform.info?.name === 'Open VSX' )?.externalId;
  }

  isExternalIdExist( product: any ): boolean {
    return !!product?.platforms?.some( ( platform: Platform ) => platform.externalId );
  }

  /**
   * Converts a formatted download string (e.g. "12.3k", "1.1M", "950")
   * back into a raw number.
   */
  parseFormattedNumber( value: any ): number {
    if ( typeof value === 'number' ) {
      return value;
    }
    if ( !value ) {
      return 0;
    }
    const normalized = String( value ).trim().replace( /,/g, '' );
    if ( /k$/i.test( normalized ) ) {
      return parseFloat( normalized ) * this.KILO;
    }
    if ( /M$/i.test( normalized ) ) {
      return parseFloat( normalized ) * this.MILLION;
    }
    const n = Number( normalized );
    return isNaN( n ) ? 0 : n;
  }

  /**
   * Animates a download counter once the real data is available.
   *
   * There is no placeholder/ramp animation: the counter stays untouched
   * (at whatever value the component initialised it to, e.g. "0") while
   * `isLoaded()` is false, then eases from 0 to the real total returned by
   * `getActualTotal()` once the data arrives.
   *
   * rAF (instead of recursive setTimeout) keeps the count fluid, syncs it
   * with the browser's paint cycle and avoids the timer pile-ups that made
   * Safari janky when many counters animated at once.
   *
   * `duration` is how long the 0 → total count takes (ms); the default suits
   * the per-theme counters, the home hero passes a slower one.
   *
   * Returns a cancel function - call it (e.g. in ngOnDestroy) to stop the
   * animation.
   */
  startDownloadAnimation(
    isLoaded: () => boolean,
    onUpdate: ( value: string ) => void,
    onComplete: ( value: string ) => void,
    getActualTotal?: () => number,
    duration = 1100
  ): () => void {
    let cancelled = false;
    let rafHandle: any;
    let timerHandle: any;

    const SETTLE_DURATION = duration;
    const easeOutCubic = ( t: number ) => 1 - Math.pow( 1 - t, 3 );

    let settleStart: number | undefined;

    const requestFrame = ( cb: ( now: number ) => void ) => {
      if ( typeof requestAnimationFrame !== 'undefined' ) {
        rafHandle = requestAnimationFrame( cb );
      } else {
        timerHandle = setTimeout( () => cb( Date.now() ), 16 );
      }
    };

    const frame = ( now: number ) => {
      if ( cancelled ) {
        return;
      }

      // Wait for the real data - no placeholder counting beforehand.
      if ( !isLoaded() ) {
        timerHandle = setTimeout( () => requestFrame( frame ), 120 );
        return;
      }

      if ( !getActualTotal ) {
        onComplete( '' );
        return;
      }

      // Count up from 0 to the real total once the data is available.
      if ( settleStart === undefined ) {
        settleStart = now;
      }
      const target = getActualTotal();
      const t = Math.min( 1, ( now - settleStart ) / SETTLE_DURATION );
      onUpdate( this.numberFormatter( Math.max( 0, Math.round( target * easeOutCubic( t ) ) ) ) );

      if ( t >= 1 ) {
        onComplete( this.numberFormatter( Math.max( 0, Math.round( target ) ) ) );
        return;
      }
      requestFrame( frame );
    };

    requestFrame( frame );

    return () => {
      cancelled = true;
      if ( rafHandle && typeof cancelAnimationFrame !== 'undefined' ) {
        cancelAnimationFrame( rafHandle );
      }
      if ( timerHandle ) {
        clearTimeout( timerHandle );
      }
    };
  }
}
